import { RULES } from '../../engine/version';
import { supabase } from '../../lib/supabase';
import type { Action } from '../../engine/actions';

export type MatchMode = 'coop' | 'versus';
export type Match = { id: string; code: string; host: string; seed: number; mode: MatchMode; status: string; created_at: string; /** Regelversion, fehlend = 1 */ ver?: number };
export type MoveRow = { match_id: string; n: number; actor: string; action: Action; created_at: string };

async function uid(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getUser(); return data.user?.id ?? null;
}

/** Legt eine gemeinsame Partie an; der Host ist automatisch Mitspieler. */
export async function createMatch(seed: number, mode: MatchMode = 'coop'): Promise<Match | string> {
  if (!supabase) return 'Nicht verbunden.';
  const me = await uid(); if (!me) return 'Nicht angemeldet.';
  let { data, error } = await supabase.from('matches').insert({ host: me, seed, mode, ver: RULES }).select('*').single();
  // Migration 0006 noch nicht ausgeführt: ohne Spalte anlegen, die Partie läuft dann nach Version 1
  if (error && /\bver\b/.test(error.message)) ({ data, error } = await supabase.from('matches').insert({ host: me, seed, mode }).select('*').single());
  if (error || !data) return error?.message ?? 'Fehler';
  const { error: e2 } = await supabase.from('match_players').insert({ match_id: data.id, user_id: me });
  return e2 ? e2.message : (data as Match);
}

/** Tritt einer Partie über ihren Code bei. */
export async function joinMatch(code: string): Promise<Match | string> {
  if (!supabase) return 'Nicht verbunden.';
  const me = await uid(); if (!me) return 'Nicht angemeldet.';
  const { data, error } = await supabase.from('matches').select('*').eq('code', code.trim().toUpperCase()).maybeSingle();
  if (error) return error.message; if (!data) return 'Keine Partie mit diesem Code.';
  // Schon Mitspieler (z. B. erneuter Beitritt nach Neuladen)? Dann ist der doppelte Eintrag kein Fehler.
  const { error: e2 } = await supabase.from('match_players').insert({ match_id: data.id, user_id: me });
  if (e2 && e2.code !== '23505') return e2.message;
  return data as Match;
}

export async function listMoves(matchId: string, after = 0): Promise<MoveRow[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.from('moves').select('*').eq('match_id', matchId).gt('n', after).order('n', { ascending: true });
  if (error) throw new Error(error.message);
  return (data as MoveRow[]) ?? [];
}

/** Schickt einen Zug. Gibt die vom Server vergebene Zugnummer zurück oder eine Fehlermeldung. */
export async function sendMove(matchId: string, actor: string, action: Action): Promise<{ n: number } | { error: string }> {
  if (!supabase) return { error: 'Nicht verbunden.' };
  const { data, error } = await supabase.from('moves').insert({ match_id: matchId, actor, action }).select('n').single();
  if (error || !data) return { error: error?.message ?? 'Unbekannter Fehler' };
  return { n: (data as { n: number }).n };
}

export async function players(matchId: string): Promise<{ user_id: string; username: string | null }[]> {
  if (!supabase) return [];
  const { data } = await supabase.from('match_players').select('user_id').eq('match_id', matchId);
  const ids = ((data ?? []) as { user_id: string }[]).map((r) => r.user_id);
  if (!ids.length) return [];
  const { data: prof } = await supabase.from('profiles').select('id, username').in('id', ids);
  const name = new Map(((prof ?? []) as { id: string; username: string | null }[]).map((p) => [p.id, p.username]));
  return ids.map((id) => ({ user_id: id, username: name.get(id) ?? null }));
}
